import { UserDto, UsersListDto } from "Api/Api";
import { EmployeeAvatar } from "Components/Shared/EmployeeAvatar";
import {
  PaginationParams,
  PrimaryDataTable,
  TableIDType,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTable";
import { StyledFlex } from "Components/Shared/StyledComponents";
import * as React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { PrimaryTooltip } from "Components/Shared/PrimaryTooltip";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";
import { DetailColumn } from "Components/Shared/PrimaryDataTable/DetailColumn";
import { Dictionary } from "lodash";
import { FilterProps } from "Components/Shared/PrimaryDataTable/PrimaryDataTableFilter";
import { OrderByProps } from "Components/Shared/PrimaryDataTable/PrimaryDataTableOrderBy";
import { useHasCurrentUserAccessRight } from "Hooks/useHasCurrentUserAccessRight";
import { AccessRightCodes } from "Components/Models/AccessRights";

type Props = {
  users: UsersListDto;
  onPageChange: (page: number, rows: number) => void;
  isLoading: boolean;
  resetTablePage?: boolean;
  filter: Dictionary<FilterProps>;
  orderBy: OrderByProps<UserDto>;
  paginationParams?: PaginationParams | null;
};

const StyledTableWrapper = styled.div`
  margin-top: ${props => props.theme.spacing(3)}px;

  thead {
    th {
      &:last-child {
        padding-left: 13px;
      }
    }
  }

  tr {
    td {
      &:nth-last-child(2) {
        width: 1%;
      }
    }
  }

  .last-login-date {
    min-width: 190px;
  }

  thead {
    .filter-popup {
      .selectbox {
        .ibis-MuiInputBase-root {
          min-width: 110px;
        }
      }
    }
  }
`;

const StyledStatus = styled.div`
  .status {
    &-circle {
      margin-left: 7px;
    }
  }
`;

export const UsersTable: React.FunctionComponent<Props> = props => {
  const {
    users,
    onPageChange,
    isLoading,
    resetTablePage,
    filter,
    orderBy,
    paginationParams,
  } = props;
  const history = useHistory();
  const { t } = useResource();

  const hasAccessRight = useHasCurrentUserAccessRight();
  const canEditUsers = hasAccessRight([AccessRightCodes.Core_Users_Edit]);

  const getStatus = (isActive: boolean) => {
    return `status-circle ${isActive ? "approved" : ""}`;
  };

  const getStatusTooltip = (isActive: boolean) => {
    return isActive
      ? t(Resources.Users.Status.Active)
      : t(Resources.Users.Status.InActive);
  };

  const onRowClick = (user: UserDto) => {
    history.push(
      `/${t(Resources.Routes.Users)}/${t(Resources.Routes.UserDetail)}/${
        user.userID
      }`,
    );
  };

  const getCell = (id: TableIDType<UserDto>, user: UserDto) => {
    switch (id) {
      case "firstName":
        return (
          <StyledFlex alignItems={"center"}>
            <EmployeeAvatar
              firstName={user.firstName}
              lastName={user.lastName}
              imgUrl={user.profilePhotoUrl}
              size={"24px"}
              fontSize={"11px"}
            />
            <div>{`${user.firstName} ${user.lastName}`}</div>
          </StyledFlex>
        );

      case "login":
        return <div>{user.login}</div>;

      case "lastLoginDate":
        return (
          <div className={"last-login-date"}>
            {formatDate(user.lastLoginDate)}
          </div>
        );

      case "isActive":
        return (
          <StyledStatus>
            <PrimaryTooltip title={getStatusTooltip(user.isActive)}>
              <div className={getStatus(user.isActive)}></div>
            </PrimaryTooltip>
          </StyledStatus>
        );

      case "detail":
        return (
          <DetailColumn
            url={`/${t(Resources.Routes.Users)}/${t(
              Resources.Routes.UserDetail,
            )}/${user.userID}`}
          />
        );

      default:
        return <div>-</div>;
    }
  };

  return (
    <StyledTableWrapper>
      <PrimaryDataTable
        isLoading={isLoading}
        rows={
          users.users?.map(user => ({
            data: user,
            rowEntityID: "userID",
          })) ?? []
        }
        columns={[
          {
            id: "firstName",
            label: t(Resources.Users.List.Name),
            width: "40%",
            filter: filter["firstAndLastName"],
            orderBy: orderBy,
          },
          {
            id: "login",
            label: t(Resources.Users.List.Email),
            filter: filter["login"],
            orderBy: orderBy,
          },
          {
            id: "lastLoginDate",
            label: t(Resources.Users.List.LastLoginDate),
            orderBy: orderBy,
          },
          {
            id: "isActive",
            label: t(Resources.Users.List.Status),
            filter: filter["isActive"],
            orderBy: orderBy,
          },
          {
            id: "detail",
            isHidden: !canEditUsers,
          },
        ]}
        getCell={getCell}
        onPageChange={onPageChange}
        totalCount={users.count}
        onRowClick={canEditUsers ? onRowClick : undefined}
        getHeaderCell={column => <div>{column.label}</div>}
        resetTablePage={resetTablePage}
        paginationParams={paginationParams}
      />
    </StyledTableWrapper>
  );
};
